<template>
  <div class="employers">
    <Form ref="formRef">
      <AddEmployerForm
        ref="employerInput"
        v-for="employer in employers"
        :key="employer.id"
        :employer="employer"
        :initialValue="employer.employerName"
        :name="`employer_${employer.id}`"
        @deleteEmployer="removeItemFromList"
        @update-modelValue="employer.employerName = $event"
      />
    </Form>
  </div>
  <div class="sc-forms-link">
    <a class="text-decoration-underline" @click="addItemToList">+Add an additional employer</a>
  </div>
</template>

<script setup lang="ts">
import { ref, computed, onMounted, defineExpose, defineProps, watch } from "vue";
import { Applicant, SurveyResponse } from "@/models/application";
import { SurveyQuestionTypeIds } from "@/config/constants";
import useHtmlUtilities from "@/use/htmlUtilities";
import { Form } from "vee-validate";
import { EmployerTypeIds, EmployerRecord } from "@/models/employer";
import AddEmployerForm from "@/components/checklist/income-verification/AddEmployerForm.vue";
import { validateStringInput } from "@/use/validation";

const { decodeHTMLEncodedStr } = useHtmlUtilities();

const props = defineProps<{ applicant: Applicant }>();

const formRef = ref<InstanceType<typeof Form>>();
const employerInput = ref<Array<InstanceType<typeof AddEmployerForm>>>(); // This will become an array of DOM elements

const employers = ref<EmployerRecord[]>([]);
const employerRecordsCount = ref<number>(1);

const employments = computed<Array<string>>(() => getInputs());

const isValid = computed<boolean>(() =>
  formRef.value && formRef.value.errors ? Object.keys(formRef.value?.errors)?.length <= 0 : false,
);

function addItemToList() {
  employerRecordsCount.value++;

  const employer = new EmployerRecord({
    id: employerRecordsCount.value,
    employerName: undefined,
    employerTypeId: EmployerTypeIds.Additional,
  });

  employers.value.push(employer);
}

function removeItemFromList(id: number) {
  const index = employers.value.findIndex((e: EmployerRecord) => e.id === id);
  if (index !== -1) {
    employers.value.splice(index, 1);
  }
}

const employerNameSurveyResponse = computed<SurveyResponse | undefined>(() =>
  props.applicant?.surveyResponses?.find(
    (s: SurveyResponse) => s.questionId === SurveyQuestionTypeIds.EmployerName,
  ),
);

function getCurrentEmployer() {
  if (employerNameSurveyResponse.value) {
    const employer = new EmployerRecord({
      id: employerRecordsCount.value,
      employerName: employerNameSurveyResponse.value?.response,
      employerTypeId: EmployerTypeIds.Current,
    });

    if (employers.value.length === 0) {
      employers.value.push(employer);
    } else {
      employers.value.splice(0, 1, employer);
      if (employerInput.value) {
        employerInput.value[0]?.setValue(employer.employerName);
      }
    }
  }
}

function getInputs() {
  return employers.value
    .filter((e: EmployerRecord) => validateStringInput(e.employerName))
    .map((e: EmployerRecord) => e.employerName as string);
}

onMounted(() => {
  getCurrentEmployer();
});

//add watch for the current employer name external change via income verification checkbox area
watch(employerNameSurveyResponse, (newVal, oldVal) => {
  if (newVal === oldVal) {
    return;
  }

  getCurrentEmployer();
});

defineExpose({
  employers,
  formRef,
  isValid,
  getInputs,
  employments,
});
</script>

<style scoped>
* {
  font-family: "Montserrat" !important;
}
a {
  margin-top: 11px;
  line-height: normal;
  text-decoration: underline;
}

a:hover {
  text-decoration: underline;
}
.container {
  display: flex;
  flex-direction: row;
  align-items: center;
  padding: 0;
}
.icon-x {
  cursor: pointer;
  margin-left: 10px;
}
span.x {
  font-family: "Montserrat";
  font-weight: 700;
}

input[type="text"]:focus {
  border-color: var(--theme-color);
}
input[type="text"] {
  border-radius: 6px;
  border: 1px solid #ced1d9;
  background: #fff;
  height: 34px;

  height: calc(1.5em + 0.75rem + 2px);
  padding: 0.375rem 0.75rem;
}

.sc-forms {
  display: flex;
  flex-direction: column;
  row-gap: 10px;
}

.sc-form {
  display: flex;
  align-items: center;
}
.sc-forms.new {
  row-gap: 5px;
}
.sc-forms.new input {
  margin-top: 5px;
}
ul {
  list-style: none;
  padding: 0;
}

ul li {
  padding: 0;
  margin: 10px 0;
}

.sc-form input {
  width: calc(100% - 26px);
}

.employers {
  display: flex;
  flex-direction: column;
}

.employers .employer {
  margin: 0 0 11px 0;
}
.sc-forms-link {
  margin-bottom: 11px;
}
.sc-forms-link::first-letter {
  font-size: 16px;
  font-weight: 700;
}
</style>
